import { useEffect, useRef, useState } from "react";
import { Cpu, X, Send, Sparkles } from "lucide-react";
import { chatStreamUrl } from "@/lib/api";

const SESSION_KEY = "srih_chat_session_id";

function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `web_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

const SUGGESTIONS = [
  "What services do you offer?",
  "Show me recent Oil & Gas projects.",
  "How do I contact Precision Contracting Network?",
  "Tell me about the Skill Training academy.",
];

export default function AiChatWidget({ open, setOpen }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "I'm PCN.ai — the AI assistant for Precision Contracting Network Company W.L.L. Ask me about our services, sectors, projects, or how to get in touch.",
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  async function send(text) {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(chatStreamUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: q }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE frames: split on "\n\n"
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const frame of parts) {
          const line = frame.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trimStart();
          if (data === "[done]") continue;
          if (data.startsWith("[error]")) {
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = { role: "assistant", content: "Sorry, I hit an error. Please try again." };
              return copy;
            });
            continue;
          }
          const decoded = data.replace(/\\n/g, "\n");
          setMessages((prev) => {
            const copy = [...prev];
            copy[copy.length - 1] = {
              role: "assistant",
              content: (copy[copy.length - 1].content || "") + decoded,
            };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: "Connection error. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  }

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          data-testid="chat-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 group"
        >
          <span className="absolute -inset-1 bg-volt/30 blur-lg opacity-70 group-hover:opacity-100 transition-opacity" />
          <span className="relative flex items-center gap-2 bg-volt text-black px-4 py-3 font-mono text-xs uppercase tracking-widest animate-pulse-volt">
            <Cpu size={16} /> Ask PCN.ai
          </span>
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-6 right-6 z-50 w-[calc(100%-3rem)] max-w-[420px] h-[600px] max-h-[85vh] bg-[#0F2952] border border-white/10 shadow-2xl flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#081936]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 border border-volt flex items-center justify-center">
                <Cpu size={14} className="text-volt" />
              </div>
              <div>
                <div className="font-display uppercase tracking-widest text-sm">PCN.ai</div>
                <div className="font-mono text-[10px] text-zinc-500 uppercase flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-volt rounded-full animate-pulse" /> Online · Streaming
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 border border-white/15 hover:border-volt hover:text-volt transition-colors"
              data-testid="chat-close"
            >
              <X size={14} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="chat-scroll flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-volt text-black font-medium"
                      : "bg-white/5 text-zinc-100 border border-white/10"
                  }`}
                >
                  {m.role === "assistant" && !m.content && streaming ? (
                    <span className="font-mono text-xs text-zinc-400">
                      thinking<span className="animate-blink">▍</span>
                    </span>
                  ) : (
                    <span className="whitespace-pre-wrap">{m.content}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs px-2 py-1 border border-white/15 text-zinc-300 hover:border-volt hover:text-volt transition-colors font-mono uppercase tracking-wide"
                  data-testid={`chat-suggestion-${s.slice(0, 10)}`}
                >
                  <Sparkles size={10} className="inline mr-1" /> {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-white/10 flex items-center gap-2 bg-[#081936]"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about our services, projects…"
              className="flex-1 bg-transparent border border-white/15 focus:border-volt outline-none px-3 py-2 text-sm placeholder:text-zinc-600"
              disabled={streaming}
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              data-testid="chat-send"
              className="btn-volt px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
